import React, { useMemo } from 'react';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import Minus from 'lucide-react/dist/esm/icons/minus';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import Percent from 'lucide-react/dist/esm/icons/percent';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Users from 'lucide-react/dist/esm/icons/users';
import Scale from 'lucide-react/dist/esm/icons/scale';
import Crown from 'lucide-react/dist/esm/icons/crown';
import { ResponsiveBar } from '@nivo/bar';
import { getTranslation } from '../../../i18n';

/**
 * Comparison Metric Card - Shows a metric with comparison indicators
 */
const ComparisonMetricCard = ({ 
  label, 
  metric, 
  format = 'percent',
  icon: Icon,
  higherIsBetter = true,
  companyNames,
  t
}) => {
  if (!metric || (metric.value1 === null && metric.value2 === null)) {
    return null;
  }

  const formatValue = (val) => {
    if (val === null || val === undefined) return 'N/A';
    if (format === 'percent') return `${val.toFixed(1)}%`;
    if (format === 'currency') return `$${(val / 1000000).toFixed(1)}M`;
    if (format === 'days') return `${val.toFixed(0)} days`;
    if (format === 'ratio') return val.toFixed(2);
    return val.toFixed(1);
  };

  const getWinnerColor = (isWinner) => {
    if (!metric.winner) return 'text-slate-400';
    return isWinner ? 'text-emerald-400' : 'text-slate-500';
  };

  const getWinnerBg = (isWinner) => {
    if (!metric.winner) return 'bg-surface-secondary';
    return isWinner ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/30 border-slate-600/30';
  };

  return (
    <div className="bg-slate-900/60 border border-subtle rounded-xl p-4 backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-violet-400" />}
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {/* Company 1 */}
        <div className={`relative rounded-lg p-3 border ${getWinnerBg(metric.winner === 'a1')}`}>
          {metric.winner === 'a1' && (
            <Crown size={14} className="absolute -top-1.5 -right-1.5 text-amber-400" />
          )}
          <div className="text-xs text-slate-500 truncate mb-1">{companyNames?.name1 || (t ? t('company1') : 'Company 1')}</div>
          <div className={`text-lg font-bold ${getWinnerColor(metric.winner === 'a1')}`}>
            {formatValue(metric.value1)}
          </div>
        </div>
        
        {/* Company 2 */}
        <div className={`relative rounded-lg p-3 border ${getWinnerBg(metric.winner === 'a2')}`}>
          {metric.winner === 'a2' && (
            <Crown size={14} className="absolute -top-1.5 -right-1.5 text-amber-400" />
          )}
          <div className="text-xs text-slate-500 truncate mb-1">{companyNames?.name2 || (t ? t('company2') : 'Company 2')}</div>
          <div className={`text-lg font-bold ${getWinnerColor(metric.winner === 'a2')}`}>
            {formatValue(metric.value2)}
          </div>
        </div>
      </div>
      
      {/* Delta indicator */}
      {metric.delta !== null && metric.delta !== undefined && (
        <div className="mt-3 pt-3 border-t border-subtle flex items-center justify-center gap-2">
          {metric.delta > 0 ? (
            <TrendingUp size={14} className={higherIsBetter ? 'text-emerald-400' : 'text-status-error'} />
          ) : metric.delta < 0 ? (
            <TrendingDown size={14} className={higherIsBetter ? 'text-status-error' : 'text-emerald-400'} />
          ) : (
            <Minus size={14} className="text-slate-500" />
          )}
          <span className="text-xs text-slate-400">
            {metric.delta > 0 ? '+' : ''}{metric.delta}% {t ? t('compDifference') : 'difference'}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Summary Comparison - Side by side executive summaries
 */
const SummaryComparison = ({ analysis1, analysis2, language }) => {
  const getSummary = (analysis) => {
    const summary = analysis?.executive_summary;
    if (typeof summary === 'object') {
      return summary[language] || summary.en || Object.values(summary)[0] || '';
    }
    return summary || '';
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {[analysis1, analysis2].map((analysis, idx) => (
        <div 
          key={idx}
          className={`bg-slate-900/60 border rounded-xl p-5 backdrop-blur-xl ${
            idx === 0 ? 'border-violet-500/30' : 'border-cyan-500/30'
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={16} className={idx === 0 ? 'text-violet-400' : 'text-cyan-400'} />
            <span className="text-sm font-semibold text-slate-200">
              {analysis?.company_info?.name || `Company ${idx + 1}`}
            </span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            {getSummary(analysis)}
          </p>
        </div>
      ))}
    </div>
  );
};

/**
 * Flags Comparison - Side by side alerts
 */
const FlagsComparison = ({ analysis1, analysis2, type, language, t }) => {
  const isRedFlag = type === 'red';
  const flags1 = analysis1?.[isRedFlag ? 'red_flags' : 'green_flags'] || [];
  const flags2 = analysis2?.[isRedFlag ? 'red_flags' : 'green_flags'] || [];
  
  const getTitle = (flag) => {
    const title = flag?.title;
    if (typeof title === 'object') {
      return title[language] || title.en || Object.values(title)[0] || '';
    }
    return title || '';
  };

  return (
    <div className="bg-slate-900/60 border border-subtle rounded-xl p-5 backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-4">
        {isRedFlag ? (
          <AlertTriangle size={18} className="text-status-error" />
        ) : (
          <CheckCircle size={18} className="text-emerald-400" />
        )}
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wide">
          {isRedFlag ? t('criticalAlertsComparison') : t('positiveIndicatorsComparison')}
        </h3>
        <div className="flex gap-2 ml-auto">
          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">
            {flags1.length} {t('compFlags')}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
            {flags2.length} {t('compFlags')}
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Company 1 Flags */}
        <div className="space-y-2">
          <div className="text-xs text-slate-500 mb-2 font-medium">
            {analysis1?.company_info?.name || t('company1')}
          </div>
          {flags1.length === 0 ? (
            <div className="text-xs text-slate-600 italic">{isRedFlag ? t('noAlertsFound') : t('noIndicatorsFound')}</div>
          ) : (
            flags1.slice(0, 3).map((flag, idx) => (
              <div 
                key={idx}
                className={`text-xs p-2 rounded-lg border ${
                  isRedFlag 
                    ? 'bg-status-error/10 border-status-error/20 text-status-error' 
                    : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                }`}
              >
                {getTitle(flag)}
              </div>
            ))
          )}
          {flags1.length > 3 && (
            <div className="text-xs text-slate-500">{t('moreItems', { count: flags1.length - 3 })}</div>
          )}
        </div>
        
        {/* Company 2 Flags */}
        <div className="space-y-2">
          <div className="text-xs text-slate-500 mb-2 font-medium">
            {analysis2?.company_info?.name || t('company2')}
          </div>
          {flags2.length === 0 ? (
            <div className="text-xs text-slate-600 italic">{isRedFlag ? t('noAlertsFound') : t('noIndicatorsFound')}</div>
          ) : (
            flags2.slice(0, 3).map((flag, idx) => (
              <div 
                key={idx}
                className={`text-xs p-2 rounded-lg border ${
                  isRedFlag 
                    ? 'bg-status-error/10 border-status-error/20 text-status-error' 
                    : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                }`}
              >
                {getTitle(flag)}
              </div>
            ))
          )}
          {flags2.length > 3 && (
            <div className="text-xs text-slate-500">{t('moreItems', { count: flags2.length - 3 })}</div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Comparison Chart - Bar chart comparing key metrics
 */
const ComparisonChart = ({ comparison, companyNames, t }) => {
  const chartData = useMemo(() => {
    const metrics = [
      { key: t('compGrossMargin'), m: comparison.gross_margin, unit: '%' },
      { key: t('compOperatingMargin'), m: comparison.operating_margin, unit: '%' },
      { key: t('compNetMargin'), m: comparison.net_margin, unit: '%' },
      { key: t('compEbitdaMargin'), m: comparison.ebitda_margin, unit: '%' },
    ].filter(d => d.m?.value1 !== null && d.m?.value2 !== null);

    return metrics.map(d => ({
      metric: d.key,
      [companyNames?.name1 || t('company1')]: d.m.value1 || 0,
      [companyNames?.name2 || t('company2')]: d.m.value2 || 0,
    }));
  }, [comparison, companyNames, t]);

  if (chartData.length === 0) return null;

  return (
    <div className="bg-slate-900/60 border border-subtle rounded-xl p-5 backdrop-blur-xl">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={18} className="text-violet-400" />
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wide">
          {t('marginComparison')}
        </h3>
      </div>
      
      <div className="h-[250px]">
        <ResponsiveBar
          data={chartData}
          keys={[companyNames?.name1 || t('company1'), companyNames?.name2 || t('company2')]}
          indexBy="metric"
          margin={{ top: 20, right: 130, bottom: 50, left: 60 }}
          padding={0.3}
          groupMode="grouped"
          colors={['#8b5cf6', '#06b6d4']} // chart color — token: brand, cyan
          borderRadius={4}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            format: v => `${v}%`
          }}
          enableGridY={true}
          gridYValues={5}
          enableLabel={false}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 120,
              itemWidth: 100,
              itemHeight: 20,
              itemTextColor: '#94a3b8',
              symbolSize: 10,
              symbolShape: 'circle',
            }
          ]}
          theme={{
            axis: {
              ticks: { text: { fill: '#94a3b8', fontSize: 11 } }
            },
            grid: { line: { stroke: '#334155', strokeWidth: 1 } },
            legends: { text: { fill: '#94a3b8', fontSize: 11 } }
          }}
          tooltip={({ id, value, color }) => (
            <div className="bg-surface-page px-3 py-2 rounded-lg border border-default shadow-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-slate-300 text-xs font-medium">{id}</span>
              </div>
              <div className="text-content font-bold text-sm mt-1">{value.toFixed(1)}%</div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

/**
 * Main Comparison View Component
 */
const ComparisonView = ({ 
  analysis1, 
  analysis2, 
  comparison, 
  onClose, 
  language = 'en' 
}) => {
  const t = (key, variables = {}) => getTranslation(language, key, variables);
  
  const companyNames = {
    name1: analysis1?.company_info?.name || t('company1'),
    name2: analysis2?.company_info?.name || t('company2'),
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-violet-500/10 via-transparent to-cyan-500/10 border-b border-subtle px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-surface-primary hover:bg-surface-elevated transition-colors"
            >
              <ArrowLeft size={18} className="text-slate-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                <Scale size={22} className="text-violet-400" />
                {t('analysisComparison')}
              </h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-violet-400 font-medium">{companyNames.name1}</span>
                <span className="mx-2">vs</span>
                <span className="text-cyan-400 font-medium">{companyNames.name2}</span>
              </p>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-violet-500" />
              <span className="text-xs text-slate-400">{companyNames.name1}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-500" />
              <span className="text-xs text-slate-400">{companyNames.name2}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Key Metrics Grid */}
        <div>
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-violet-400" />
            {t('keyPerformanceIndicators')}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ComparisonMetricCard
              label={t('compGrossMargin')}
              metric={comparison.gross_margin}
              icon={Percent}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compOperatingMargin')}
              metric={comparison.operating_margin}
              icon={Percent}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compNetMargin')}
              metric={comparison.net_margin}
              icon={Percent}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compEbitdaMargin')}
              metric={comparison.ebitda_margin}
              icon={Percent}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compCurrentRatio')}
              metric={comparison.current_ratio}
              format="ratio"
              icon={Scale}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compDsoDays')}
              metric={comparison.dso_days}
              format="days"
              icon={Clock}
              higherIsBetter={false}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compPersonnelCosts')}
              metric={comparison.personnel_costs}
              icon={Users}
              higherIsBetter={false}
              companyNames={companyNames}
              t={t}
            />
            <ComparisonMetricCard
              label={t('compTotalRevenue')}
              metric={comparison.revenue}
              format="currency"
              icon={DollarSign}
              companyNames={companyNames}
              t={t}
            />
          </div>
        </div>

        {/* Comparison Chart */}
        <ComparisonChart comparison={comparison} companyNames={companyNames} t={t} />

        {/* Executive Summaries */}
        <div>
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-4">
            {t('executiveSummaries')}
          </h2>
          <SummaryComparison 
            analysis1={analysis1} 
            analysis2={analysis2} 
            language={language}
          />
        </div>

        {/* Flags Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FlagsComparison 
            analysis1={analysis1} 
            analysis2={analysis2} 
            type="red"
            language={language}
            t={t}
          />
          <FlagsComparison 
            analysis1={analysis1} 
            analysis2={analysis2} 
            type="green"
            language={language}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
